// Bond Delegation Manager — create, list, and revoke bond delegations.
//
// Engine routes used (all live as of seitiate#35, merged 2026-05-04):
//   GET  /api/v1/bond-delegations?owner_id=  → list (Lane 3 follow-up #5)
//   POST /api/v1/bond-delegations            → create
//   POST /api/v1/bond-delegations/{id}/revoke
//
// The owner_id field on create + the owner_id query param both require
// the engine-side User UUID (NOT the Firebase uid). That UUID arrives via
// `context.engineUserId` (sourced from /auth/me's `user_id` field —
// engine PR #36). When it's null (older engine deployment), the
// "Resolve" affordance is disabled and listing is hidden.

import { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import {
  createBondDelegation,
  listBondDelegations,
  revokeBondDelegation,
} from '@/api/bondDelegations';
import type {
  BondDelegationListResponse,
  BondDelegationRead,
  CreateDelegationBody,
} from '@/api/agentBoardTypes';
import { EngineHttpError } from '@/lib/engineHttp';
import {
  V1_DEFAULT_ALLOWED_ACTIONS,
  V1_DEFAULT_GATED_ACTIONS,
} from '@/lib/agentActionTaxonomy';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

function csvToList(s: string): string[] {
  return s
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

function delegationsCacheKey(ownerId: string | null): string | null {
  return ownerId ? `agent-board:delegations:owner:${ownerId}` : null;
}

function BondDelegationManagerWidget({ context }: WidgetProps) {
  const { mutate: globalMutate } = useSWRConfig();

  // Create form state. owner_id is left blank by default; the operator
  // either pastes a UUID or clicks "Resolve" to auto-fill from
  // context.engineUserId (engine /auth/me).
  const [ownerId, setOwnerId] = useState('');
  const [agentIdentityId, setAgentIdentityId] = useState('');
  const [bondId, setBondId] = useState('');
  const [allowedActions, setAllowedActions] = useState(
    V1_DEFAULT_ALLOWED_ACTIONS.join(', '),
  );
  const [gatedActions, setGatedActions] = useState(
    V1_DEFAULT_GATED_ACTIONS.join(', '),
  );
  const [expiresAt, setExpiresAt] = useState('');
  const [maxConcurrent, setMaxConcurrent] = useState('');
  const [autonomyTier, setAutonomyTier] = useState('');
  const [governancePosture, setGovernancePosture] = useState('');

  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdDelegation, setCreatedDelegation] = useState<BondDelegationRead | null>(null);

  // Revoke form state.
  const [revokeDelegationId, setRevokeDelegationId] = useState('');
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokedDelegation, setRevokedDelegation] = useState<BondDelegationRead | null>(null);

  // Listing — gated on having the engine User UUID. Without it we can't
  // call /bond-delegations?owner_id= for this user.
  const listingKey = delegationsCacheKey(context.engineUserId);
  const {
    data: listing,
    error: listingError,
    isLoading: listingLoading,
  } = useSWR<BondDelegationListResponse>(
    listingKey,
    () => listBondDelegations({ owner_id: context.engineUserId as string }),
    { revalidateOnFocus: false },
  );

  function refreshListing() {
    if (listingKey) void globalMutate(listingKey);
  }

  function pasteOwnerFromContext() {
    if (context.engineUserId) {
      setOwnerId(context.engineUserId);
    }
  }

  function loadIntoRevokeForm(d: BondDelegationRead) {
    setRevokeDelegationId(d.id);
    setRevokeReason('');
    setRevokeError(null);
    setRevokedDelegation(null);
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateSubmitting(true);
    setCreateError(null);
    setCreatedDelegation(null);

    const body: CreateDelegationBody = {
      owner_id: ownerId.trim(),
      agent_identity_id: agentIdentityId.trim(),
      bond_id: bondId.trim(),
      allowed_actions: csvToList(allowedActions),
      gated_actions: csvToList(gatedActions),
    };
    if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
    if (maxConcurrent) {
      const n = Number(maxConcurrent);
      if (Number.isFinite(n) && n > 0) body.max_concurrent_runs = Math.floor(n);
    }
    if (autonomyTier.trim()) body.autonomy_tier = autonomyTier.trim();
    if (governancePosture.trim()) body.governance_posture = governancePosture.trim();

    try {
      const result = await createBondDelegation(body);
      setCreatedDelegation(result);
      refreshListing();
    } catch (err) {
      setCreateError(
        err instanceof EngineHttpError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error',
      );
    } finally {
      setCreateSubmitting(false);
    }
  }

  async function submitRevoke(e: React.FormEvent) {
    e.preventDefault();
    setRevokeSubmitting(true);
    setRevokeError(null);
    setRevokedDelegation(null);
    try {
      const result = await revokeBondDelegation(revokeDelegationId.trim(), {
        revoked_by: context.userId ?? 'unknown',
        reason: revokeReason.trim() || undefined,
      });
      setRevokedDelegation(result);
      refreshListing();
    } catch (err) {
      setRevokeError(
        err instanceof EngineHttpError
          ? `${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error',
      );
    } finally {
      setRevokeSubmitting(false);
    }
  }

  function copyToClipboard(text: string) {
    void navigator.clipboard?.writeText(text);
  }

  return (
    <div id="widget-bond-delegation-manager" className="sp-widget">
      <div className="sp-widget-header">
        <span>Bond Delegation Manager</span>
      </div>

      <div className="sp-widget-body" style={{ padding: 12, fontSize: 12 }}>
        {/* ── Create ─────────────────────────────────────── */}
        <section style={{ marginBottom: 16 }}>
          <SectionTitle>Create</SectionTitle>
          <form onSubmit={submitCreate} style={{ display: 'grid', gap: 6 }}>
            <Field label="Owner ID (engine UUID — NOT your Firebase uid)">
              <div style={{ display: 'flex', gap: 6 }}>
                <Input
                  value={ownerId}
                  onChange={setOwnerId}
                  required
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
                <button
                  type="button"
                  onClick={pasteOwnerFromContext}
                  disabled={!context.engineUserId}
                  title={
                    context.engineUserId
                      ? 'Fill from /auth/me (your engine User UUID)'
                      : 'Engine /auth/me did not return user_id. Update the engine deployment to seitiate#36 or later.'
                  }
                  style={{
                    fontSize: 11,
                    padding: '2px 10px',
                    border: '1px solid var(--sp-border)',
                    background: 'transparent',
                    color: context.engineUserId ? 'var(--sp-accent)' : 'var(--sp-text-dim)',
                    borderRadius: 3,
                    cursor: context.engineUserId ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Resolve
                </button>
              </div>
            </Field>
            <Field label="Agent identity ID (UUID)">
              <Input
                value={agentIdentityId}
                onChange={setAgentIdentityId}
                required
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Field>
            <Field label="Bond ID">
              <Input value={bondId} onChange={setBondId} required />
            </Field>
            <Field label="Allowed actions (comma-separated)">
              <Input value={allowedActions} onChange={setAllowedActions} />
            </Field>
            <Field label="Gated actions (comma-separated)">
              <Input value={gatedActions} onChange={setGatedActions} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <Field label="Expires at (optional)">
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  style={inputStyle}
                />
              </Field>
              <Field label="Max concurrent runs (optional)">
                <Input value={maxConcurrent} onChange={setMaxConcurrent} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <Field label="Autonomy tier (optional)">
                <Input value={autonomyTier} onChange={setAutonomyTier} />
              </Field>
              <Field label="Governance posture (optional)">
                <Input value={governancePosture} onChange={setGovernancePosture} />
              </Field>
            </div>
            <div>
              <button
                type="submit"
                disabled={createSubmitting || !ownerId.trim() || !agentIdentityId.trim() || !bondId.trim()}
                style={primaryButtonStyle(createSubmitting)}
              >
                {createSubmitting ? '…' : 'Create delegation'}
              </button>
            </div>
            {createError ? (
              <div style={{ color: 'var(--sp-error)', fontSize: 11 }}>{createError}</div>
            ) : null}
            {createdDelegation ? (
              <div
                style={{
                  marginTop: 4,
                  padding: 8,
                  border: '1px solid var(--sp-ok)',
                  borderRadius: 3,
                  fontSize: 11,
                }}
              >
                <div style={{ color: 'var(--sp-ok)', marginBottom: 4 }}>Delegation created.</div>
                <div className="mono" style={{ wordBreak: 'break-all' }}>
                  {createdDelegation.id}
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(createdDelegation.id)}
                  style={{
                    marginTop: 4,
                    fontSize: 10,
                    padding: '2px 8px',
                    border: '1px solid var(--sp-border)',
                    background: 'transparent',
                    color: 'var(--sp-text-muted)',
                    borderRadius: 3,
                    cursor: 'pointer',
                  }}
                >
                  Copy ID
                </button>
              </div>
            ) : null}
          </form>
        </section>

        {/* ── Revoke ─────────────────────────────────────── */}
        <section style={{ marginBottom: 16 }}>
          <SectionTitle>Revoke</SectionTitle>
          <form onSubmit={submitRevoke} style={{ display: 'grid', gap: 6 }}>
            <Field label="Delegation ID">
              <Input
                value={revokeDelegationId}
                onChange={setRevokeDelegationId}
                required
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </Field>
            <Field label="Reason (optional)">
              <Input value={revokeReason} onChange={setRevokeReason} />
            </Field>
            <div>
              <button
                type="submit"
                disabled={revokeSubmitting || !revokeDelegationId.trim()}
                style={dangerButtonStyle(revokeSubmitting)}
              >
                {revokeSubmitting ? '…' : 'Revoke delegation'}
              </button>
            </div>
            {revokeError ? (
              <div style={{ color: 'var(--sp-error)', fontSize: 11 }}>{revokeError}</div>
            ) : null}
            {revokedDelegation ? (
              <div style={{ color: 'var(--sp-ok)', fontSize: 11 }}>
                Revoked at{' '}
                {revokedDelegation.revoked_at
                  ? new Date(revokedDelegation.revoked_at).toLocaleString()
                  : 'now'}
                .
              </div>
            ) : null}
          </form>
        </section>

        {/* ── My Delegations ─────────────────────────────── */}
        <section>
          <SectionTitle>My Delegations</SectionTitle>
          <MyDelegationsList
            engineUserId={context.engineUserId}
            listing={listing}
            error={listingError}
            isLoading={listingLoading}
            onLoadIntoRevoke={loadIntoRevokeForm}
            onCopyId={copyToClipboard}
          />
        </section>
      </div>
    </div>
  );
}

interface MyDelegationsListProps {
  engineUserId: string | null;
  listing: BondDelegationListResponse | undefined;
  error: unknown;
  isLoading: boolean;
  onLoadIntoRevoke: (d: BondDelegationRead) => void;
  onCopyId: (id: string) => void;
}

function MyDelegationsList({
  engineUserId,
  listing,
  error,
  isLoading,
  onLoadIntoRevoke,
  onCopyId,
}: MyDelegationsListProps) {
  if (!engineUserId) {
    return (
      <div
        style={{
          padding: 8,
          border: '1px dashed var(--sp-border)',
          borderRadius: 3,
          fontSize: 11,
          color: 'var(--sp-text-muted)',
        }}
      >
        Listing requires the engine to expose your User UUID via{' '}
        <span className="mono">/auth/me.user_id</span>. Update the engine
        deployment to <span className="mono">seitiate#36</span> or later, then
        sign out and back in.
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ color: 'var(--sp-error)', fontSize: 11 }}>
        {error instanceof EngineHttpError
          ? `${error.status}: ${error.message}`
          : error instanceof Error
            ? error.message
            : 'Failed to load delegations.'}
      </div>
    );
  }
  if (isLoading || !listing) {
    return (
      <div style={{ color: 'var(--sp-text-muted)', fontSize: 11 }}>
        Loading…
      </div>
    );
  }
  if (listing.items.length === 0) {
    return (
      <div style={{ color: 'var(--sp-text-dim)', fontSize: 11 }}>
        No delegations recorded for your owner UUID.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {listing.items.map((d) => (
        <div
          key={d.id}
          style={{
            padding: 6,
            border: '1px solid var(--sp-border)',
            borderRadius: 3,
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 6,
            fontSize: 11,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span className="mono" style={{ color: 'var(--sp-text)' }}>
                {d.bond_id}
              </span>
              <span
                style={{
                  fontSize: 9,
                  padding: '0 4px',
                  borderRadius: 2,
                  background:
                    d.status === 'active'
                      ? 'var(--sp-ok)'
                      : d.status === 'revoked'
                        ? 'var(--sp-error)'
                        : 'var(--sp-text-dim)',
                  color: 'var(--sp-bg, #000)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                {d.status}
              </span>
            </div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
              {d.id.slice(0, 8)}… · agent {d.agent_identity_id.slice(0, 8)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
            <button
              type="button"
              onClick={() => onCopyId(d.id)}
              style={{
                fontSize: 10,
                padding: '2px 8px',
                border: '1px solid var(--sp-border)',
                background: 'transparent',
                color: 'var(--sp-text-muted)',
                borderRadius: 3,
                cursor: 'pointer',
              }}
            >
              Copy ID
            </button>
            {d.status === 'active' ? (
              <button
                type="button"
                onClick={() => onLoadIntoRevoke(d)}
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  border: '1px solid var(--sp-error)',
                  background: 'transparent',
                  color: 'var(--sp-error)',
                  borderRadius: 3,
                  cursor: 'pointer',
                }}
              >
                Revoke
              </button>
            ) : null}
          </div>
        </div>
      ))}
      {listing.count > listing.items.length ? (
        <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', padding: '2px 0' }}>
          Showing {listing.items.length} of {listing.count}.
        </div>
      ) : null}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: '1px solid var(--sp-border)',
  borderRadius: 3,
  padding: '4px 6px',
  color: 'var(--sp-text)',
  fontFamily: 'inherit',
  fontSize: 12,
};

function Input({
  value,
  onChange,
  required,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 10, color: 'var(--sp-text-dim)', marginBottom: 2 }}>{label}</div>
      {children}
    </label>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: 'var(--sp-text-dim)',
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function primaryButtonStyle(submitting: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    padding: '4px 12px',
    border: '1px solid var(--sp-accent)',
    background: 'var(--sp-accent)',
    color: 'var(--sp-bg, #000)',
    borderRadius: 3,
    cursor: submitting ? 'not-allowed' : 'pointer',
  };
}

function dangerButtonStyle(submitting: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    padding: '4px 12px',
    border: '1px solid var(--sp-error)',
    background: 'transparent',
    color: 'var(--sp-error)',
    borderRadius: 3,
    cursor: submitting ? 'not-allowed' : 'pointer',
  };
}

export const bondDelegationManagerWidget: WidgetDefinition = {
  id: 'bond-delegation-manager',
  name: 'Bond Delegation Manager',
  description: 'Create, list, and revoke bond delegations for the active owner.',
  icon: 'key',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 6, h: 8 },
  minSize: { w: 4, h: 6 },
  component: BondDelegationManagerWidget,
};
